import chinese from "./locales/zh-CN.json" with { type: "json" };
import { readPref, writePref } from "./prefs.ts";

export type Locale = "en-US" | "zh-CN";
export type LanguagePreference = "system" | Locale;

export function resolveLocale(preference: string | null, languages: readonly string[] = []): Locale {
  if (preference === "en-US" || preference === "zh-CN") return preference;
  for (const language of languages) {
    if (/^zh(?:-|$)/i.test(language)) return "zh-CN";
    if (/^en(?:-|$)/i.test(language)) return "en-US";
  }
  return "en-US";
}

export const languagePreference = (): LanguagePreference => {
  // A URL fallback keeps switching usable when browser storage is blocked.
  let requested: string | null = null;
  try { requested = new URL(window.location.href).searchParams.get("lang"); } catch { /* No browser URL in Node. */ }
  const value = requested === "en-US" || requested === "zh-CN" ? requested : readPref("language");
  return value === "en-US" || value === "zh-CN" ? value : "system";
};

// Resolve before modules initialize their labels and navigation. Changing the
// language reloads the same URL so no screen retains labels from another locale.
const activeLocale = resolveLocale(languagePreference(), typeof window === "undefined" ? [] : navigator.languages);
export const locale = (): Locale => activeLocale;

export function translate(message: string, ...values: (string | number | null | undefined)[]): string {
  const template = activeLocale === "zh-CN" && Object.hasOwn(chinese, message)
    ? chinese[message as keyof typeof chinese] : message;
  // Substitute once: braces, dollars and English in source data stay exact.
  return template.replace(/\{(\d+)\}/g, (match, index: string) => Number(index) < values.length ? String(values[Number(index)]) : match);
}

export function applyLanguage() {
  document.documentElement.lang = activeLocale;
}

export function setLanguage(preference: LanguagePreference) {
  writePref("language", preference === "system" ? null : preference);
  const url = new URL(window.location.href);
  url.searchParams.delete("lang");
  if (preference !== "system" && readPref("language") !== preference) url.searchParams.set("lang", preference);
  window.location.replace(url.href);
}
